import React from "react";
import IRMA from "@models/IRMA";
import OwButton from "@components/shared/OwButton/OwButton";
import RmaStatusEnum from "@models/RmaStatusEnum";
import { OwIconTooltip } from "@components/shared/OwIconTooltip/OwIconTooltip";
import "./RmaActionButtons.scss";
import { useIntl } from "react-intl";
import { ControlBar } from "@components/shared/ControlBar/ControlBar";

type Props = {
    rma: IRMA;
    onAuthorize: () => void;
    onReceiveItems: () => void;
    onRecordInspection: () => void;
    onProcess: () => void;
    onCancel: () => void;
    onEdit: () => void;
    onDelete: () => void;
    loading?: boolean;
};

export const RmaActionButtons = (props: Props) => {
    const { rma, loading } = props;
    const intl = useIntl();

    // Check if all line items are fully received
    const areAllItemsFullyReceived = (): boolean => {
        const lineItems = rma.RMA_Line_Items || [];
        if (lineItems.length === 0) return false;
        return lineItems.every((item) => (item.qty_received || 0) >= item.qty_returned);
    };

    // Check if all received items are fully inspected
    const areAllItemsFullyInspected = (): boolean => {
        const lineItems = rma.RMA_Line_Items || [];
        if (lineItems.length === 0) return false;

        return lineItems.every((item) => {
            const qtyReceived = item.qty_received || 0;
            const inspections = item.RMA_Line_Item_Inspections || [];
            const qtyInspected = inspections.reduce((sum, insp) => sum + insp.quantity, 0);
            return qtyInspected >= qtyReceived;
        });
    };

    const getProcessButtonDisabledReason = (): string | undefined => {
        if (!areAllItemsFullyReceived()) {
            return intl.formatMessage({ id: "all_items_must_be_fully_received_before_processing" });
        }
        if (!areAllItemsFullyInspected()) {
            return intl.formatMessage({ id: "all_items_must_be_fully_inspected_before_processing" });
        }
        return undefined;
    };

    const renderDraftActions = () => (
        <>
            <OwButton title="authorize_rma" fn={props.onAuthorize} active={loading} className="btn-primary me-2" />
            <OwButton title="edit" fn={props.onEdit} active={loading} className="btn-secondary me-2" />
            <OwButton title="delete" fn={props.onDelete} active={loading} className="btn-danger me-2" />
            <OwButton title="cancel_rma" fn={props.onCancel} active={loading} className="btn-warning" />
        </>
    );

    const renderAuthorizedActions = () => (
        <>
            <OwButton title="receive_items" fn={props.onReceiveItems} active={loading} className="btn-primary me-2" />
            <OwButton title="cancel_rma" fn={props.onCancel} active={loading} className="btn-warning me-2" />
        </>
    );

    const renderReceivedActions = () => {
        const processDisabled = !areAllItemsFullyReceived() || !areAllItemsFullyInspected();
        const disabledReason = getProcessButtonDisabledReason();

        return (
            <>
                <OwButton title="receive_items" fn={props.onReceiveItems} active={loading} className="btn-primary me-2" />
                {/* <OwButton title="record_inspection" fn={props.onRecordInspection} active={loading} className="btn-info me-2" /> */}
                <div className="d-inline-flex align-items-center">
                    <OwButton
                        title="process_rma"
                        fn={props.onProcess}
                        active={loading}
                        className="btn-success me-2"
                        disabled={processDisabled}
                        hoverTitle={disabledReason}
                    />
                    {processDisabled && disabledReason && (
                        <OwIconTooltip iconClassName="fas fa-exclamation-triangle text-warning me-2" tooltipContent={disabledReason} />
                    )}
                </div>
                <OwButton title="cancel_rma" fn={props.onCancel} active={loading} className="btn-warning" />
            </>
        );
    };

    const renderButtons = () => {
        switch (rma.rma_status_id) {
            case RmaStatusEnum.DRAFT:
                return renderDraftActions();
            case RmaStatusEnum.AUTHORIZED:
                return renderAuthorizedActions();
            case RmaStatusEnum.RECEIVED:
                return renderReceivedActions();
            case RmaStatusEnum.PROCESSED:
            case RmaStatusEnum.CANCELLED:
                // No actions for processed or cancelled RMAs
                return null;
            default:
                return null;
        }
    };

    const showControlBar = renderButtons() !== null;

    return (
        <div className="RmaActionButtons mb-4">
            {showControlBar && (
                <ControlBar
                    sections={[
                        {
                            name: "actions",
                            elements: [renderButtons()],
                        },
                    ]}
                />
            )}
        </div>
    );
};
